import type {
  AccessCatalogItemPublic,
  AccessNodePublic,
  AccessRecordPublic,
  ApplicationPublic,
} from '@accounts/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { STR } from '../../../../i18n';
import {
  listGrants,
  retireAccessCatalogItem,
  updateAccessCatalogItem,
  type UpdateAccessCatalogItemBody,
} from '../../../../lib/api';
import { formatDate } from '../../../../lib/format';
import { queryKeys } from '../../../../lib/queryKeys';
import { showToast } from '../../../../lib/toast';
import { Modal, ModalParagraph, ModalProse } from '../../../../ui/Modal';
import { Callout, Chip, EmptyRow } from '../../../../ui/Primitives';
import {
  buttonStyle,
  detailNameStyle,
  modalButtonStyle,
  monoStyle,
  primaryButtonStyle,
  rowActionStyle,
} from '../../../../ui/styles';
import { itemLabel, RECORD_STATUS_TONE, recordStatusLabel } from '../accessFormat';
import { AddRecordDialog, HistoryList, RecordDialog } from '../RecordDialogs';

import { Drawer, FoldCard } from './Layout';
import { errorText, useInvalidateAccess } from './queries';
import { type RowDraft, RowFields } from './RowFields';

type Dialog =
  | { kind: 'retire' }
  | { kind: 'record'; record: AccessRecordPublic }
  | { kind: 'addRecord' };

/**
 * A catalog row, opened beside the list (FINAL demo `itemDetail`): its header, a one-line
 * summary, then three collapsible sections — Row settings (open), People with access and
 * Row history. Retire is offered and confirmed; nothing here deletes.
 *
 * People with access follows the row's kind: an outside system's people are its access
 * records (each opens the record and its history); a suite app's people are the app's
 * live grants, read from Grants rather than stored twice.
 *
 * A view-only reader (`readOnly`, HR or IT with "See Catalog") gets no Row history: it is
 * the audit log, which the server gates to administrators. A record opens view only.
 */
export function CatalogRowDrawer({
  item,
  nodes,
  applications,
  records,
  readOnly = false,
  onClose,
}: {
  item: AccessCatalogItemPublic;
  nodes: readonly AccessNodePublic[];
  applications: readonly ApplicationPublic[];
  records: readonly AccessRecordPublic[];
  /**
   * A view-only reader (SIR-7864): no Retire, no Add person, the row settings locked, no
   * Row history, and a record opens view only.
   */
  readOnly?: boolean;
  onClose: () => void;
}) {
  const invalidate = useInvalidateAccess();
  const [open, setOpen] = useState({ settings: true, people: false, history: false });
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const retired = item.retiredAt !== null;
  const application = item.application;

  const grants = useQuery({
    queryKey: queryKeys.grantsForApplication(application?.id ?? '', false),
    queryFn: () => listGrants({ applicationId: application?.id ?? '', includeRevoked: false }),
    enabled: application !== null,
  });

  const holders = records.filter((record) => record.catalogItem.id === item.id);
  const peopleCount = application === null ? holders.length : (grants.data?.data.length ?? 0);
  const logins =
    application === null
      ? new Set(
          holders
            .map((record) => record.login?.toLowerCase() ?? null)
            .filter((login): login is string => login !== null),
        ).size
      : null;

  const retire = useMutation({
    mutationFn: () => retireAccessCatalogItem(item.id),
    onSuccess: async () => {
      setDialog(null);
      await invalidate();
      showToast(STR.access.catalog.rowRetired(item.name));
    },
    onError: (failure) => {
      setDialog(null);
      showToast(errorText(failure), 'warn');
    },
  });

  // Stable, so an open dialog's focus handling is not re-run by this drawer's re-renders.
  const closeDialog = useCallback(() => {
    setDialog(null);
  }, []);

  return (
    <Drawer label={item.name} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <span className="mark" aria-hidden="true">
            {item.name.slice(0, 1).toUpperCase()}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={detailNameStyle}>{itemLabel(item.path, item.name)}</div>
            <div className="tx-muted" style={{ fontSize: 13 }}>
              {application === null
                ? STR.access.catalog.outsideSystem
                : STR.access.catalog.connectedTo(application.name)}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 9, flexWrap: 'wrap' }}>
              {retired ? (
                <Chip tone="neutral">{STR.access.catalog.retiredOn(formatDate(item.retiredAt))}</Chip>
              ) : (
                <Chip tone={item.requestable ? 'positive' : 'attention'}>
                  {item.requestable ? STR.access.catalog.chipRequestable : STR.access.catalog.chipHidden}
                </Chip>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flex: 'none' }}>
            {retired || readOnly ? null : (
              <button
                type="button"
                className="tbtn tbtn-danger"
                style={{ ...buttonStyle, borderColor: 'var(--border-strong)' }}
                onClick={() => {
                  setDialog({ kind: 'retire' });
                }}
              >
                {STR.access.catalog.retireRow}
              </button>
            )}
            <button
              type="button"
              className="ghost"
              style={buttonStyle}
              aria-label={STR.access.catalog.drawerClose}
              onClick={onClose}
            >
              {STR.common.close}
            </button>
          </div>
        </div>

        <div className="tx-subtle" style={{ fontSize: 12.5 }}>
          {STR.access.catalog.summary(peopleCount, logins)}
        </div>

        <FoldCard
          title={STR.access.catalog.settingsTitle}
          aside={STR.access.catalog.settingsAside(
            item.requestable,
            application?.name ?? null,
            item.hrCanGrant,
          )}
          open={open.settings}
          onToggle={() => {
            setOpen((state) => ({ ...state, settings: !state.settings }));
          }}
        >
          {/* Keyed on the saved row, so a landed save (or a change made elsewhere) re-seeds
              the draft by remounting rather than leaving a stale form open. */}
          <RowSettings
            key={item.updatedAt}
            item={item}
            nodes={nodes}
            applications={applications}
            readOnly={readOnly}
          />
        </FoldCard>

        <FoldCard
          title={STR.access.catalog.peopleTitle}
          count={peopleCount}
          open={open.people}
          onToggle={() => {
            setOpen((state) => ({ ...state, people: !state.people }));
          }}
        >
          {application === null ? (
            <>
              {holders.length === 0 ? (
                <EmptyRow>{STR.access.catalog.nobodyHasIt}</EmptyRow>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {[...holders]
                    .sort((a, b) => a.user.name.localeCompare(b.user.name))
                    .map((record) => (
                      <li key={record.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <button
                          type="button"
                          className="row"
                          onClick={() => {
                            setDialog({ kind: 'record', record });
                          }}
                          style={{
                            width: '100%',
                            display: 'flex',
                            gap: 14,
                            alignItems: 'center',
                            padding: '9px 18px',
                            border: 'none',
                            background: 'transparent',
                            color: 'var(--text)',
                            textAlign: 'left',
                            cursor: 'pointer',
                          }}
                        >
                          <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>
                            {record.user.name}
                          </span>
                          <span className="tx-subtle" style={{ ...monoStyle, fontSize: 11.5 }}>
                            {record.login ?? STR.accessCommon.noLogin}
                          </span>
                          <Chip tone={RECORD_STATUS_TONE[record.status]} size="sm">
                            {recordStatusLabel(record.status)}
                          </Chip>
                        </button>
                      </li>
                    ))}
                </ul>
              )}
              {retired || readOnly ? null : (
                <div style={{ padding: '10px 18px' }}>
                  <button
                    type="button"
                    className="ghost"
                    style={rowActionStyle}
                    onClick={() => {
                      setDialog({ kind: 'addRecord' });
                    }}
                  >
                    {STR.access.catalog.addPerson}
                  </button>
                </div>
              )}
            </>
          ) : grants.isError ? (
            <EmptyRow>{STR.errors.couldNotLoad}</EmptyRow>
          ) : (grants.data?.data.length ?? 0) === 0 ? (
            <EmptyRow>{grants.isPending ? STR.common.loading : STR.access.catalog.nobodyHasIt}</EmptyRow>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {[...(grants.data?.data ?? [])]
                .sort((a, b) => a.user.name.localeCompare(b.user.name))
                .map((grant) => (
                  <li
                    key={grant.id}
                    style={{
                      display: 'flex',
                      gap: 14,
                      alignItems: 'center',
                      padding: '9px 18px',
                      borderBottom: '1px solid var(--border)',
                    }}
                  >
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>
                      {grant.user.name}
                    </span>
                    <span className="tx-subtle" style={{ fontSize: 11.5 }}>
                      {STR.access.catalog.grantedOn(formatDate(grant.grantedAt))}
                    </span>
                  </li>
                ))}
            </ul>
          )}
        </FoldCard>

        {readOnly ? null : (
          <FoldCard
            title={STR.access.catalog.historyTitle}
            open={open.history}
            onToggle={() => {
              setOpen((state) => ({ ...state, history: !state.history }));
            }}
          >
            <HistoryList targetId={item.id} />
          </FoldCard>
        )}
      </div>

      {dialog?.kind === 'retire' && !readOnly ? (
        <Modal
          title={STR.access.catalog.retireRowTitle(item.name)}
          onClose={closeDialog}
          footer={
            <>
              <button type="button" className="ghost" style={modalButtonStyle} onClick={closeDialog}>
                {STR.common.cancel}
              </button>
              <button
                type="button"
                className="dbtn"
                style={modalButtonStyle}
                disabled={retire.isPending}
                onClick={() => {
                  retire.mutate();
                }}
              >
                {STR.access.catalog.retireConfirm}
              </button>
            </>
          }
        >
          <ModalProse>
            <ModalParagraph>{STR.access.catalog.retireRowBody}</ModalParagraph>
          </ModalProse>
        </Modal>
      ) : null}
      {dialog?.kind === 'record' ? (
        <RecordDialog record={dialog.record} readOnly={readOnly} onClose={closeDialog} />
      ) : null}
      {dialog?.kind === 'addRecord' && !readOnly ? (
        <AddRecordDialog catalogItemId={item.id} onClose={closeDialog} />
      ) : null}
    </Drawer>
  );
}

/** The Row settings form; saves only the fields that changed. */
function RowSettings({
  item,
  nodes,
  applications,
  readOnly,
}: {
  item: AccessCatalogItemPublic;
  nodes: readonly AccessNodePublic[];
  applications: readonly ApplicationPublic[];
  readOnly: boolean;
}) {
  const invalidate = useInvalidateAccess();
  const retired = item.retiredAt !== null;
  const locked = retired || readOnly;
  const saved: RowDraft = {
    name: item.name,
    nodeId: item.nodeId,
    applicationId: item.application?.id ?? null,
    requestable: item.requestable,
    hrCanGrant: item.hrCanGrant,
  };
  const [draft, setDraft] = useState<RowDraft>(saved);
  const [error, setError] = useState<string | null>(null);

  const patch: UpdateAccessCatalogItemBody = {
    ...(draft.name.trim() !== saved.name ? { name: draft.name.trim() } : {}),
    ...(draft.nodeId !== saved.nodeId ? { nodeId: draft.nodeId } : {}),
    ...(draft.applicationId !== saved.applicationId ? { applicationId: draft.applicationId } : {}),
    ...(draft.requestable !== saved.requestable ? { requestable: draft.requestable } : {}),
    ...(draft.hrCanGrant !== saved.hrCanGrant ? { hrCanGrant: draft.hrCanGrant } : {}),
  };
  const dirty = Object.keys(patch).length > 0;

  const save = useMutation({
    mutationFn: () => updateAccessCatalogItem(item.id, patch),
    onSuccess: async (response) => {
      await invalidate();
      showToast(STR.access.catalog.rowSaved(itemLabel(response.item.path, response.item.name)));
    },
    onError: (failure) => {
      setError(errorText(failure));
    },
  });

  return (
    <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      {retired ? (
        <Callout tone="neutral">{STR.access.catalog.readOnlyRetired}</Callout>
      ) : readOnly ? (
        <Callout tone="neutral">{STR.access.catalog.readOnlyViewer}</Callout>
      ) : null}
      <RowFields
        draft={draft}
        onChange={(next) => {
          setDraft(next);
          setError(null);
        }}
        nodes={nodes}
        applications={applications}
        disabled={locked}
        idPrefix={`row-${item.id}`}
      />
      {error === null ? null : (
        <Callout tone="danger" role="alert">
          {error}
        </Callout>
      )}
      {locked ? null : (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            className="ghost"
            style={buttonStyle}
            disabled={!dirty || save.isPending}
            onClick={() => {
              setDraft(saved);
              setError(null);
            }}
          >
            {STR.common.cancel}
          </button>
          <button
            type="button"
            className="prim"
            style={primaryButtonStyle}
            disabled={!dirty || save.isPending}
            onClick={() => {
              if (draft.name.trim() === '') {
                setError(STR.access.catalog.nameRequired);
                return;
              }
              save.mutate();
            }}
          >
            {STR.common.saveChanges}
          </button>
        </div>
      )}
    </div>
  );
}
